import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Skeleton,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslate } from "@refinedev/core";
import { usePermissions } from "../../hooks";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { ProfileAdminListItem } from "../../interfaces";
import { formatPhoneDisplay } from "../../utils/phone";
import { apiVersion } from "../../utils/api";
import { PaginationFooter } from "../../components/pagination/PaginationFooter";

type SearchField = "name" | "id" | "email" | "phone";
type OrderField = "created_date" | "updated_date" | "name" | "followers_count" | "trust_points" | "ranking";
type SortOrder = "asc" | "desc";

const formatDateTime = (timestamp?: number): string => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export const ProfilesList: React.FC = () => {
  const { hasFullAccess } = usePermissions();
  const t = useTranslate();
  const navigate = useNavigate();

  // Search and filter state
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState<SearchField>("name");
  const [orderField, setOrderField] = useState<OrderField>("created_date");
  const [order, setOrder] = useState<SortOrder>("desc");

  // Access control - redirect if not admin or founder
  if (!hasFullAccess) {
    return <Navigate to="/" replace />;
  }

  // Build extra params for API
  const extraParams = useMemo(() => {
    const params: Record<string, string> = {
      order_field: orderField,
      order: order,
    };

    if (searchText.trim()) {
      params.search = searchText.trim();
      params.search_field = searchField;
    }

    return params;
  }, [searchText, searchField, orderField, order]);

  // Fetch data using the paginated list hook
  const { items, total, loading, error, page, setPage } = usePaginatedList<ProfileAdminListItem>({
    endpoint: `admin/${apiVersion}/profiles`,
    limit: 12,
    extraParams,
  });

  // Helper text for search field
  const getSearchHelperText = (): string => {
    switch (searchField) {
      case "email":
        return t("profiles.search.emailHelper");
      case "phone":
        return t("profiles.search.phoneHelper");
      case "name":
        return t("profiles.search.nameHelper");
      case "id":
        return t("profiles.search.idHelper");
      default:
        return "";
    }
  };

  // Handle row click - navigate to profile details
  const handleRowClick = (profileId: string) => {
    navigate(`/profile/${profileId}`);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Calculate total pages
  const totalPages = Math.ceil(total / 12);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        {t("profiles.titles.list")}
      </Typography>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
          alignItems: { xs: "stretch", md: "flex-start" },
        }}
      >
        {/* Search Field */}
        <FormControl sx={{ minWidth: 200, flex: 1, maxWidth: { md: 400 } }}>
          <TextField
            placeholder={t("profiles.search.placeholder")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          <FormHelperText>{getSearchHelperText()}</FormHelperText>
        </FormControl>

        {/* Search Field Selector */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="search-field-label">{t("profiles.search.fieldLabel")}</InputLabel>
          <Select
            labelId="search-field-label"
            value={searchField}
            label={t("profiles.search.fieldLabel")}
            onChange={(e) => setSearchField(e.target.value as SearchField)}
          >
            <MenuItem value="name">{t("profiles.search.fields.name")}</MenuItem>
            <MenuItem value="id">{t("profiles.search.fields.id")}</MenuItem>
            <MenuItem value="email">{t("profiles.search.fields.email")}</MenuItem>
            <MenuItem value="phone">{t("profiles.search.fields.phone")}</MenuItem>
          </Select>
        </FormControl>

        {/* Sort Field Selector */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="sort-field-label">{t("profiles.sort.fieldLabel")}</InputLabel>
          <Select
            labelId="sort-field-label"
            value={orderField}
            label={t("profiles.sort.fieldLabel")}
            onChange={(e) => setOrderField(e.target.value as OrderField)}
          >
            <MenuItem value="created_date">{t("profiles.sort.fields.created_date")}</MenuItem>
            <MenuItem value="updated_date">{t("profiles.sort.fields.updated_date")}</MenuItem>
            <MenuItem value="name">{t("profiles.sort.fields.name")}</MenuItem>
            <MenuItem value="followers_count">{t("profiles.sort.fields.followers_count")}</MenuItem>
            <MenuItem value="trust_points">{t("profiles.sort.fields.trust_points")}</MenuItem>
            <MenuItem value="ranking">{t("profiles.sort.fields.ranking")}</MenuItem>
          </Select>
        </FormControl>

        {/* Sort Direction Toggle */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="direction-label" shrink>{t("profiles.sort.direction")}</InputLabel>
          <Box sx={{ mt: 1.5 }}>
            <ToggleButtonGroup
              value={order}
              exclusive
              onChange={(_, newOrder) => {
                if (newOrder !== null) setOrder(newOrder);
              }}
              size="medium"
              sx={{
                '& .MuiToggleButton-root': {
                  py: 0.5,
                  px: 1.5,
                  fontSize: '0.875rem',
                  height: '42px',
                }
              }}
            >
              <ToggleButton value="asc" aria-label={t("profiles.sort.asc")}>
                <ArrowUpIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                {t("profiles.sort.asc")}
              </ToggleButton>
              <ToggleButton value="desc" aria-label={t("profiles.sort.desc")}>
                <ArrowDownIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                {t("profiles.sort.desc")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </FormControl>
      </Box>

      {/* Error State */}
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: "error.lighter", borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("profiles.table.photo")}</TableCell>
              <TableCell>{t("profiles.table.name")}</TableCell>
              <TableCell>{t("profiles.table.id")}</TableCell>
              <TableCell>{t("profiles.table.email")}</TableCell>
              <TableCell>{t("profiles.table.phone")}</TableCell>
              <TableCell>{t("profiles.table.admin")}</TableCell>
              <TableCell>{t("profiles.table.created_date")}</TableCell>
              <TableCell>{t("profiles.table.updated_date")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Loading State - Skeleton Rows */}
            {loading &&
              Array.from({ length: 12 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Skeleton variant="circular" width={40} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={150} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={140} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={140} />
                  </TableCell>
                </TableRow>
              ))}

            {/* Empty State */}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t("profiles.empty.message")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("profiles.empty.suggestion")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Data Rows */}
            {!loading &&
              items.map((profile) => (
                <TableRow
                  key={profile.id}
                  hover
                  onClick={() => handleRowClick(profile.id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Avatar src={profile.photo?.url} alt={profile.name} sx={{ width: 40, height: 40 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {profile.name || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {profile.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{profile.email || "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatPhoneDisplay(profile.phone)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {(profile.type === "FOUNDER" || profile.is_admin) && (
                      <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(profile.created_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(profile.updated_date)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Footer */}
      {!loading && items.length > 0 && (
        <PaginationFooter
          page={page}
          limit={12}
          total={total}
          onPageChange={setPage}
          onLimitChange={() => {}}
          showPageSizeSelector={false}
        />
      )}
    </Box>
  );
};
